import type { ReactNode, CSSProperties } from "react";

export function Surface({ children, className = "", style }: { children: ReactNode; className?: string; style?: CSSProperties }) {
  return (
    <div className={className} style={{
      background: "var(--c-card)",
      border: "1px solid var(--c-border)",
      borderRadius: "var(--ui-card-radius)",
      boxShadow: "var(--ui-card-shadow)",
      ...style,
    }}>{children}</div>
  );
}

export function PageHeader({ title, meta, actions }: {
  title: string; meta?: ReactNode; actions?: ReactNode;
}) {
  // Title and action share a row; the description runs beneath both. With the
  // description inside the title column, a phone pushed the action onto a
  // row of its own.
  return (
    <div className="ui-pagehead">
      <div className="ui-pagehead-row">
        <h1 className="ui-display ui-pagehead-title">{title}</h1>
        {actions && <div className="ui-pagehead-actions">{actions}</div>}
      </div>
      {meta && <div className="ui-pagehead-meta">{meta}</div>}
    </div>
  );
}

export function EmptyState({
  title, body, hint, icon, actions, compact,
}: {
  title: string; body: string; hint: string; icon?: ReactNode; actions?: ReactNode; compact?: boolean;
}) {
  return (
    <div style={{
      padding: compact ? "var(--ui-space-6) var(--ui-space-4)" : "var(--ui-space-8)",
      textAlign: "center",
      display: "grid",
      justifyItems: "center",
      gap: "var(--ui-space-2)",
    }}>
      {icon && <div className="nl-empty-tile">{icon}</div>}
      <div style={{ fontSize: "var(--ui-text-section)", fontWeight: 600, color: "var(--c-t1)" }} className="nl-text-balance">{title}</div>
      <div style={{ fontSize: "var(--ui-text-control)", color: "var(--c-t3)", maxWidth: "42ch" }} className="nl-text-balance">{body}</div>
      <div style={{ fontSize: "var(--ui-text-meta)", color: "var(--c-t4)", maxWidth: "46ch" }}>{hint}</div>
      {actions && <div style={{ marginTop: "var(--ui-space-3)", display: "flex", gap: "var(--ui-space-2)", justifyContent: "center" }}>{actions}</div>}
    </div>
  );
}

export function CoverageChip({
  status, children, title,
}: {
  status: "complete" | "partial" | "mixed" | "stale" | "demo";
  children: ReactNode;
  title?: string;
}) {
  const tone = {
    complete: { bg: "var(--c-tint-emerald)", fg: "var(--c-success-text)" },
    partial: { bg: "var(--c-tint-amber)", fg: "var(--c-warn-text)" },
    mixed: { bg: "var(--c-tint-blue)", fg: "var(--c-info)" },
    stale: { bg: "var(--c-tint-amber)", fg: "var(--c-warn-text)" },
    demo: { bg: "var(--c-tint-amber)", fg: "var(--c-warn-text)" },
  }[status];
  return (
    <span
      title={title}
      style={{
        background: tone.bg, color: tone.fg,
        fontSize: "var(--ui-text-micro)", fontWeight: 600, letterSpacing: ".02em",
        padding: "3px 8px",
        borderRadius: 5, whiteSpace: "nowrap",
        border: "1px solid var(--c-border)",
      }}
    >{children}</span>
  );
}

const ARABIC = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;

/**
 * Text that may be Arabic or English (titles, subjects, names). Its direction
 * follows its own first strong character, and Arabic is marked lang="ar" so it
 * takes the Arabic body font and punctuation lands on the correct side.
 */
export function Bidi({ children }: { children: ReactNode }) {
  const arabic = typeof children === "string" && ARABIC.test(children);
  return <bdi dir="auto" lang={arabic ? "ar" : undefined}>{children}</bdi>;
}

export function DemoChip() {
  return (
    <span style={{
      background: "var(--c-tint-amber)", color: "var(--c-warn-text)",
      fontSize: "var(--ui-text-micro)", fontWeight: 600, letterSpacing: ".02em",
      padding: "3px 10px",
      borderRadius: 5, whiteSpace: "nowrap",
    }} title="Sample data for preview — no live warehouse or sending provider">
      Demo
    </span>
  );
}

/** "draft" → "Draft", "consent missing" → "Consent missing". */
function sentence(c: ReactNode): ReactNode {
  return typeof c === "string" && c ? c.charAt(0).toUpperCase() + c.slice(1) : c;
}

/**
 * A status, drawn as the one status pill (StatusPill) so every page uses the
 * same shape and the same meaning per colour, in sentence case. It used to be
 * its own boxed chip, and labels arrived as "draft", "ready", "Pass"…
 */
export function StatusBadge({ tone, children }: { tone: "success" | "info" | "warn" | "muted"; children: ReactNode }) {
  const pill: Record<typeof tone, StatusTone> = { success: "success", info: "active", warn: "review", muted: "neutral" };
  return <StatusPill tone={pill[tone]}>{sentence(children)}</StatusPill>;
}

export function SectionHeader({ title, meta, actions, compact }: {
  title: string; meta?: ReactNode; actions?: ReactNode; compact?: boolean;
}) {
  return (
    <div style={{
      padding: compact ? "var(--ui-space-3) var(--ui-space-4) var(--ui-space-3)" : "var(--ui-space-4)",
      display: "flex", justifyContent: "space-between", alignItems: "flex-start",
      gap: "var(--ui-space-3)", flexWrap: "wrap",
    }}>
      <div>
        <div style={{ fontSize: "var(--ui-text-section)", fontWeight: 500, color: "var(--c-t1)" }}>{title}</div>
        {meta && <div style={{ fontSize: "var(--ui-text-meta)", color: "var(--c-t3)", marginTop: 2 }}>{meta}</div>}
      </div>
      {actions && <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>{actions}</div>}
    </div>
  );
}

export function PrimaryButton({
  children, disabled, onClick, title, type = "button",
}: {
  children: ReactNode; disabled?: boolean; onClick?: () => void; title?: string; type?: "button" | "submit";
}) {
  return (
    // Standard §5: the one solid --brand-500 pill on the screen. Styling is in
    // newsletter.css so :hover and the scoped "only one primary" rule work.
    <button type={type} disabled={disabled} onClick={onClick} title={title} className="nl-btn-primary">
      {children}
    </button>
  );
}

export function SecondaryButton({ children, disabled, onClick, title }: { children: ReactNode; disabled?: boolean; onClick?: () => void; title?: string }) {
  return (
    // Standard §5: white pill, 1px --brand-100 border, --brand-500 text.
    <button type="button" disabled={disabled} onClick={onClick} title={title} className="nl-btn-secondary">
      {children}
    </button>
  );
}

export function Pill({ active, onClick, children, title }: {
  active?: boolean; onClick?: () => void; children: ReactNode; title?: string;
}) {
  return (
    // A pressed chip is a state, not an action, so it takes the tint rather
    // than the solid blue the standard reserves for the one primary action.
    <button type="button" className="ui-pill" onClick={onClick} title={title} aria-pressed={active}>
      {children}
    </button>
  );
}

/** A label that is not a status (a role, "Added"): one neutral chip, sentence case. */
export function Tag({ children, tone = "muted" }: { children: ReactNode; tone?: "accent" | "purple" | "teal" | "muted" }) {
  return <span className={`ui-tag${tone === "accent" ? " is-accent" : ""}`}>{sentence(children)}</span>;
}

/**
 * A status pill (standard §5): 26px, a 6px dot and a 13/600 label — never
 * colour alone. The mapping is fixed: done is success, needs attention is
 * review, live or scheduled is active, not started is neutral, failed is error.
 */
export type StatusTone = "success" | "review" | "active" | "neutral" | "error";

export function StatusPill({ tone, children }: { tone: StatusTone; children: ReactNode }) {
  return (
    <span className="ui-status" data-tone={tone}>
      <span className="ui-status-dot" aria-hidden />
      {children}
    </span>
  );
}

/** Newsletter status → the standard's pill. */
export function issueStatusPill(status: string): { tone: StatusTone; label: string } {
  switch (status) {
    case "sent": return { tone: "success", label: "Sent" };
    case "approved": return { tone: "success", label: "Approved" };
    case "in_review": return { tone: "review", label: "In review" };
    case "scheduled": return { tone: "active", label: "Scheduled" };
    case "draft": return { tone: "neutral", label: "Draft" };
    default: return { tone: "neutral", label: status.replace(/_/g, " ") };
  }
}
